import React, { useState, useCallback, useRef, useEffect } from 'react';

interface DateOfBirthInputProps {
    /**
     * Input ID for label association and form submission
     */
    id: string;
    /**
     * Display name for the input
     */
    name: string;
    /**
     * Locale to determine date format (EU: DD/MM/YYYY, US: MM/DD/YYYY)
     */
    locale: string;
    /**
     * Initial value in YYYY-MM-DD format (backend format)
     */
    initialValue?: string;
    /**
     * Callback when date changes (receives YYYY-MM-DD format)
     */
    onChange?: (isoDate: string, isValid: boolean) => void;
    /**
     * Callback on blur event
     */
    onBlur?: (isoDate: string, isValid: boolean, isFilled?: boolean) => void;
    /**
     * CSS class name
     */
    className?: string;
    /**
     * Placeholder text (optional, defaults to format mask)
     */
    placeholder?: string;
    /**
     * Whether to prevent paste operations
     */
    preventPaste?: boolean;
}

/**
 * Determines if locale uses DMY format (European) or MDY format (US)
 */
const isDMYLocale = (locale: string): boolean => {
    const normalizedLocale = locale.toLowerCase().replace('-', '');
    const dmyLocales = ['eu', 'it', 'enit', 'fr', 'enfr', 'de', 'ende', 'engb', 'gb', 'ensg', 'sg'];
    return dmyLocales.includes(normalizedLocale);
};

/**
 * Determines the date separator for a given locale
 * German locales use '.' instead of '/'
 */
const getDateSeparator = (locale: string): string => {
    const normalizedLocale = locale.toLowerCase().replace('-', '');
    switch (normalizedLocale) {
        case 'de':
        case 'ende':
            return '.';
        default:
            return '/';
    }
};

/**
 * Gets the date format mask based on locale
 */
export const getDateMask = (locale: string): string => {
    const separator = getDateSeparator(locale);
    switch (locale.toLowerCase().replace('-', '')) {
        case 'it':
        case 'enit':
            return `GG${separator}MM${separator}AAAA`;
        case 'eu':
        case 'gb':
        case 'sg':
        case 'ende':
        case 'enfr':
        case 'engb':
        case 'ensg':
            return `DD${separator}MM${separator}YYYY`;
        case 'fr':
            return `JJ${separator}MM${separator}AAAA`;
        case 'de':
            return `TT${separator}MM${separator}JJJJ`;
        case 'us':
        case 'enus':
            return `MM${separator}DD${separator}YYYY`;
        default:
            return isDMYLocale(locale) ? `DD${separator}MM${separator}YYYY` : `MM${separator}DD${separator}YYYY`;
    }
};

/**
 * Validates if a date is valid (handles leap years, month boundaries, etc.)
 */
const isValidDate = (day: number, month: number, year: number): boolean => {
    if (month < 1 || month > 12) {
        return false;
    }
    if (day < 1) {
        return false;
    }

    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Check for leap year
    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    if (month === 2 && isLeapYear) {
        daysInMonth[1] = 29;
    }

    return day <= daysInMonth[month - 1];
};

/**
 * Converts display format (DD/MM/YYYY, DD.MM.YYYY, MM/DD/YYYY, or MM.DD.YYYY) to ISO format (YYYY-MM-DD)
 */
const toISOFormat = (displayValue: string, isDMY: boolean): string | null => {
    // Extract only digits from the display value (handles both / and . as separators)
    const digits = displayValue.replace(/\D/g, '');

    if (digits.length !== 8) {
        return null;
    }

    let day: string, month: string, year: string;

    if (isDMY) {
        // DD/MM/YYYY or DD.MM.YYYY
        day = digits.substring(0, 2);
        month = digits.substring(2, 4);
        year = digits.substring(4, 8);
    } else {
        // MM/DD/YYYY or MM.DD.YYYY
        month = digits.substring(0, 2);
        day = digits.substring(2, 4);
        year = digits.substring(4, 8);
    }

    // Validate the date components
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt(year, 10);

    if (!isValidDate(dayNum, monthNum, yearNum)) {
        return null;
    }

    return `${year}-${month}-${day}`;
};

/**
 * Converts ISO format (YYYY-MM-DD) to display format (DD/MM/YYYY, DD.MM.YYYY, MM/DD/YYYY, or MM.DD.YYYY)
 */
const fromISOFormat = (isoValue: string, isDMY: boolean, locale: string): string => {
    const parts = isoValue.split('-');
    if (parts.length !== 3) {
        return '';
    }

    const [year, month, day] = parts;
    const separator = getDateSeparator(locale);

    if (isDMY) {
        return `${day}${separator}${month}${separator}${year}`;
    } else {
        return `${month}${separator}${day}${separator}${year}`;
    }
};

/**
 * DateOfBirthInput Component
 *
 * A masked text input for date of birth with:
 * - Locale-aware formatting (DD/MM/YYYY for EU, MM/DD/YYYY for US)
 * - Automatic separator insertion
 * - Numeric-only input
 * - Date validation
 * - Backend normalization to YYYY-MM-DD
 *
 * @example
 * ```tsx
 * <DateOfBirthInput
 *   id="birthday"
 *   name="birthday"
 *   locale="it"
 *   onChange={(isoDate, isValid) => console.log(isoDate)}
 * />
 * ```
 */
const DateOfBirthInput: React.FC<DateOfBirthInputProps> = ({
    id,
    locale,
    initialValue,
    onChange,
    onBlur,
    className = '',
    placeholder,
    preventPaste = true
}) => {
    const isDMY = isDMYLocale(locale);
    const separator = getDateSeparator(locale);
    const mask = getDateMask(locale);
    const inputRef = useRef<HTMLInputElement>(null);

    // Initialize display value from ISO format if provided
    const [displayValue, setDisplayValue] = useState(() => {
        if (initialValue) {
            return fromISOFormat(initialValue, isDMY, locale);
        }
        return '';
    });

    // Hidden field to store the normalized ISO date
    const [isoValue, setIsoValue] = useState(initialValue || '');

    /**
     * Formats the input value with automatic separator insertion
     * Adds separator as soon as a block is completed (after 2nd and 4th digit)
     * Uses the appropriate separator based on locale (/ for most, . for German)
     */
    const formatValue = useCallback(
        (digits: string): string => {
            if (digits.length === 0) {
                return '';
            }

            // Add separators after 2nd digit (DD/ or MM/) and after 4th digit (MM/DD/ or DD/MM/)
            // When block is complete, separator is immediately added
            let result = '';
            for (let i = 0; i < digits.length && i < 8; i++) {
                result += digits[i];
                // Add separator after completing 2nd digit (day/month block) or 4th digit (month block)
                if ((i === 1 || i === 3) && digits.length > i) {
                    result += separator;
                }
            }

            return result;
        },
        [separator]
    );

    useEffect(() => {
        const hiddenInput = document.getElementById(id) as HTMLInputElement | null;
        if (hiddenInput) {
            hiddenInput.value = isoValue;
            hiddenInput.setAttribute('value', isoValue);
        }
    }, [isoValue]);

    const getFormattedValue = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            try {
                const rawValue = event.target.value;

                // Extract only digits from the input
                const digits = rawValue.replace(/\D/g, '');

                // Limit to 8 digits (DDMMYYYY or MMDDYYYY)
                const limitedDigits = digits.slice(0, 8);

                // Format with separators
                const formatted = formatValue(limitedDigits);

                // Update display value
                setDisplayValue(formatted);

                // Validate and convert to ISO format
                const iso = toISOFormat(formatted, isDMY);
                const isValid = iso !== null;

                setIsoValue(iso || '');

                return { value: iso || '', isValid, isFilled: !!iso || !!formatted };
            } catch (error) {
                return { value: '', isValid: false };
            }
        },
        [formatValue, isDMY, onChange]
    );
    /**
     * Handles input change with masking logic
     */
    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { value: iso, isValid } = getFormattedValue(event);

            if (onChange) {
                onChange(iso, isValid);
            }
        },
        [getFormattedValue, onChange]
    );

    /**
     * Handles paste events
     */
    const handlePaste = useCallback(
        (event: React.ClipboardEvent<HTMLInputElement>) => {
            if (preventPaste) {
                event.preventDefault();
            }
        },
        [preventPaste]
    );

    /**
     * Handles backspace key to allow deletion of separators
     * When user presses backspace after a separator, removes both the separator and the preceding digit
     * Works with both / and . separators
     */
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Backspace') {
                const input = event.currentTarget;
                const cursorPos = input.selectionStart ?? 0;
                const currentValue = input.value;

                // Check if cursor is right after a separator
                if (cursorPos > 0 && currentValue[cursorPos - 1] === separator) {
                    event.preventDefault();

                    // Remove the separator and the digit before it
                    const newValue = currentValue.slice(0, cursorPos - 2) + currentValue.slice(cursorPos);

                    // Simulate change event by directly updating the display value
                    setDisplayValue(newValue);

                    // Extract digits and format
                    const digits = newValue.replace(/\D/g, '');
                    const formatted = formatValue(digits);

                    // Update display and ISO value
                    setDisplayValue(formatted);
                    const iso = toISOFormat(formatted, isDMY);
                    const isValid = iso !== null;
                    setIsoValue(iso || '');

                    if (onChange) {
                        onChange(iso || '', isValid);
                    }

                    // Position cursor correctly
                    setTimeout(() => {
                        if (inputRef.current) {
                            // Cursor should be after the last digit
                            const newPosition = formatted.length > cursorPos - 2 ? cursorPos - 1 : formatted.length;
                            inputRef.current.setSelectionRange(newPosition, newPosition);
                        }
                    }, 0);
                }
            }
        },
        [formatValue, isDMY, onChange, separator]
    );

    /**
     * Handles blur events
     */
    const handleBlur = useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            const { value: iso, isValid, isFilled } = getFormattedValue(event);

            if (onBlur) {
                onBlur(iso, isValid, isFilled);
            }
        },
        [getFormattedValue, onBlur]
    );

    return (
        <>
            <input
                ref={inputRef}
                type='text'
                value={displayValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={handleBlur}
                className={className}
                placeholder={placeholder || mask}
                maxLength={10} // DD/MM/YYYY or MM/DD/YYYY = 10 chars
                inputMode='numeric'
                autoComplete='off'
            />
        </>
    );
};

export default DateOfBirthInput;
